// ─── cTrader Order Alert Auto-Start ─────────────────────────
// Starts the order-alert watcher for every connected cTrader account,
// independent of the Copy Trade engine. Runs once per session after
// auth resolves, and re-syncs on tab focus to pick up newly connected
// accounts. Stops (releases streams) on logout.

import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import {
  startOrderAlertWatcher,
  stopOrderAlertWatcher,
  type OrderAlertAccount,
} from '@/lib/ctrader-order-alert';

export function useCtraderOrderAlert(userId: string | null) {
  const startedRef = useRef(false);

  const sync = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('ctrader_gateway_accounts')
        .select('account_id, account_name');

      if (error) throw error;

      const accounts: OrderAlertAccount[] = (data || [])
        .map((r) => ({
          accountId: parseInt(String(r.account_id), 10),
          accountName: String(r.account_name || r.account_id),
        }))
        .filter((a) => !Number.isNaN(a.accountId));

      if (accounts.length > 0) {
        startOrderAlertWatcher(accounts);
        console.log(`[OrderAlert] Watching ${accounts.length} cTrader account(s)`);
      }
    } catch (err) {
      console.warn('[OrderAlert] Failed to load cTrader accounts:', err);
    }
  }, []);

  useEffect(() => {
    if (!userId) {
      stopOrderAlertWatcher();
      startedRef.current = false;
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    void sync();
  }, [userId, sync]);

  // Re-sync on tab focus so newly connected accounts get watched.
  useEffect(() => {
    if (!userId) return;
    const onFocus = () => { void sync(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [userId, sync]);
}